import PropTypes from "prop-types";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { END_POINTS } from "../../../Helper/Constant";
import { getApiRequest } from "../../../services/getApiRequest";
import { findMentionUserId, MENTION_REGEX } from "../../chatMentions/chatMentionUtils";
import "./ChatMessageText.scss";
import "../VipMessage/VipMessage.scss";
import "../../chatMentions/chatMentions.scss";

const URL_PATTERN = /(\bhttps?:\/\/[^\s<]+|\bwww\.[^\s<]+)/gi;
const MENTION_PATTERN = new RegExp(MENTION_REGEX.source, "g");
const TOKEN_PATTERN = new RegExp(
  `${MENTION_PATTERN.source}|${URL_PATTERN.source}`,
  "gi"
);

const normalizeLinkHref = (url) =>
  url.startsWith("www.") ? `https://${url}` : url;

const parseMessageParts = (text) => {
  if (!text) return [{ type: "text", value: "" }];

  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(TOKEN_PATTERN.source, TOKEN_PATTERN.flags);
  let match = regex.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith("@")) {
      parts.push({ type: "mention", value: token, userName: match[1] });
    } else {
      parts.push({ type: "link", value: token });
    }
    lastIndex = regex.lastIndex;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }

  return parts.length ? parts : [{ type: "text", value: text }];
};

const renderLine = (line, lineKey, currentUserName, onMentionClick) =>
  parseMessageParts(line).map((part, index) => {
    if (part.type === "link") {
      return (
        <a
          key={`${lineKey}-link-${index}`}
          href={normalizeLinkHref(part.value)}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-message-link"
          onClick={(e) => e.stopPropagation()}
        >
          {part.value}
        </a>
      );
    }
    if (part.type === "mention") {
      const isSelf =
        currentUserName &&
        String(part.userName).toLowerCase() ===
          String(currentUserName).toLowerCase();
      return (
        <span
          key={`${lineKey}-mention-${index}`}
          dir="ltr"
          role="link"
          tabIndex={0}
          className={`chat-mention${isSelf ? " is-self" : ""}`}
          onClick={(event) => onMentionClick?.(event, part.userName)}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            onMentionClick?.(event, part.userName);
          }}
        >
          {part.value}
        </span>
      );
    }
    return (
      <span key={`${lineKey}-text-${index}`} className="chat-message-text-part">
        {part.value}
      </span>
    );
  });

const ChatMessageText = ({
  text,
  paragraphClassName = "chat-message-line",
  currentUserName: currentUserNameProp,
  users = [],
  inline = false,
}) => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user.userData);
  const currentUserName = currentUserNameProp || userData?.userName;

  const openMentionProfile = useCallback(
    async (event, userName) => {
      event.stopPropagation();
      event.preventDefault();
      const localId = findMentionUserId([userData, ...users], userName);
      if (localId) {
        navigate(`/consumer-Profile/${localId}`);
        return;
      }

      try {
        const response = await getApiRequest(
          `${END_POINTS.FRIENDS_SEARCH}?search=${encodeURIComponent(userName)}`
        );
        const list = response?.users || response?.friends || response?.data || [];
        const id = findMentionUserId(Array.isArray(list) ? list : [], userName);
        if (id) navigate(`/consumer-Profile/${id}`);
      } catch {
        // Mention stays in the message when the profile cannot be resolved.
      }
    },
    [navigate, userData, users]
  );

  if (!text) return null;

  if (inline) {
    return (
      <span className={paragraphClassName}>
        {renderLine(
          String(text).replace(/\n/g, " "),
          "inline",
          currentUserName,
          openMentionProfile
        )}
      </span>
    );
  }

  return text.split("\n").map((line, lineIndex) => (
    <p key={`line-${lineIndex}`} className={paragraphClassName}>
      {renderLine(line, lineIndex, currentUserName, openMentionProfile)}
    </p>
  ));
};

ChatMessageText.propTypes = {
  text: PropTypes.string,
  paragraphClassName: PropTypes.string,
  currentUserName: PropTypes.string,
  users: PropTypes.array,
  inline: PropTypes.bool,
};

export default ChatMessageText;
